import type { Location } from 'react-router-dom'
import type { Lang } from '@/i18n'
import { langFromPath } from '@/i18n'

export type NavFromState = { from?: string; fromLabel?: string }

export function currentPath(location: Location): string {
  return `${location.pathname}${location.search || ''}`
}

export function getFromState(location: Location): NavFromState {
  const st = location.state
  if (st && typeof st === 'object' && 'from' in st) {
    const from = (st as NavFromState).from
    if (typeof from === 'string' && from.startsWith('/')) {
      return {
        from,
        fromLabel: typeof (st as NavFromState).fromLabel === 'string'
          ? (st as NavFromState).fromLabel
          : undefined,
      }
    }
  }
  return {}
}

function langOf(path: string, fallback: Lang = 'de'): Lang {
  return langFromPath(path) || fallback
}

/** Human label for a prior route (used in back button). */
export function backHintForPath(path: string, lang: Lang = 'de'): string {
  const l = langOf(path, lang)
  const p = path.split('?')[0] || path
  const isEn = l === 'en'
  if (new RegExp(`^/${l}/attack-chains/[^/]+`).test(p)) return isEn ? 'to Attack Chain' : 'zur Attack Chain'
  if (p === `/${l}/attack-chains` || p.startsWith(`/${l}/attack-chains?`)) return isEn ? 'to Attack Chains' : 'zu Attack Chains'
  if (new RegExp(`^/${l}/tools/[^/]+`).test(p)) return isEn ? 'to tool' : 'zum Tool'
  if (p === `/${l}/tools` || p.startsWith(`/${l}/tools?`)) return isEn ? 'to Tools DB' : 'zur Tools-DB'
  if (p === `/${l}/explore` || p.startsWith(`/${l}/explore?`)) return isEn ? 'to Atlas' : 'zum Atlas'
  if (p === `/${l}/` || p === `/${l}`) return isEn ? 'to Home' : 'zur Home'
  if (p === `/${l}/status`) return isEn ? 'to Status' : 'zum Status'
  if (
    new RegExp(`^/${l}/[^/]+/[^/]+`).test(p) &&
    !p.startsWith(`/${l}/attack-chains`) &&
    !p.startsWith(`/${l}/tools`)
  ) {
    return isEn ? 'to technique' : 'zur Technik'
  }
  return ''
}

export function fallbackParent(pathname: string, lang: Lang = 'de'): string {
  const l = langOf(pathname, lang)
  if (new RegExp(`^/${l}/tools/`).test(pathname)) return `/${l}/tools`
  if (new RegExp(`^/${l}/attack-chains/`).test(pathname)) return `/${l}/attack-chains`
  if (pathname.startsWith(`/${l}/explore`)) return `/${l}/`
  if (pathname.startsWith(`/${l}/status`)) return `/${l}/`
  if (new RegExp(`^/${l}/[^/]+/[^/]+`).test(pathname)) return `/${l}/explore`
  return `/${l}/`
}

export function hasSameAppHistory(): boolean {
  try {
    const idx = (window.history.state as { idx?: number } | null)?.idx
    return typeof idx === 'number' && idx > 0
  } catch {
    return false
  }
}
